/**
 * Runtime environment variable schema. Loaded once at server start by the API
 * via api/src/config/env.ts. Kept in shared/ because tests, jobs, and the
 * Vite-side build step all read from the same source of truth.
 *
 * AI provider selection (spec §25, issue #16):
 *   `AI_PROVIDER=openai` requires `AI_API_KEY` to be a non-empty string. The
 *   key is mode-conditional: when `AI_PROVIDER=mock`, the key is ignored and
 *   may safely be empty (the mock provider is the supported test substrate
 *   per the project's "OpenAI for production, mock for tests" decision).
 *   `AI_MODEL` is optional in both modes; the OpenAI provider falls back to
 *   `gpt-4o-mini` when not set.
 *
 * Signup gating (issue #268):
 *   `SIGNUP_ALLOWED_DOMAINS` — comma-separated list of email domains allowed
 *   to sign up. Domain match is exact, case-insensitive. Replaces the
 *   previous `SIGNUP_ALLOWED_EMAIL` single-email allowlist.
 *   `ADMIN_EMAIL_DOMAINS` — comma-separated list of domains whose new users
 *   are assigned the `ADMIN` role at signup (else `OPERATOR`).
 *   Both are required, non-secret strings; parsing into a domain list lives
 *   in `parseDomainsList()` in `api/src/config/envSchema.ts`.
 */

import { z } from "zod";

const trimmed = z.string().trim();
const requiredString = trimmed.min(1);

export const envSchema = z
  .object({
    NODE_ENV: z.enum(["development", "test", "production"]).default("development"),
    PORT: z.coerce.number().int().positive().default(3000),

    DATABASE_URL: requiredString,

    SESSION_SECRET: requiredString.min(32),
    JWT_SECRET: requiredString.min(32),

    APP_HOSTNAME: requiredString,
    SIGNUP_ALLOWED_DOMAINS: requiredString,
    ADMIN_EMAIL_DOMAINS: requiredString,
    SIGNUP_CODE: requiredString,

    AI_PROVIDER: z.enum(["mock", "openai"]).default("mock"),
    AI_API_KEY: trimmed.optional().default(""),
    AI_MODEL: trimmed.optional().default(""),
    AI_IMAGE_MODEL: trimmed.optional().default(""),
    AI_MONTHLY_BUDGET_USD: z.coerce.number().nonnegative().default(50),
    AI_RATE_LIMIT_PER_MINUTE: z.coerce.number().int().positive().default(10),

    IMAGE_PROVIDER: z.enum(["mock", "pexels"]).default("mock"),
    IMAGE_API_KEY: trimmed.optional().default(""),
    UPLOADS_DIR: requiredString.default("./data/uploads"),

    PUBLIC_BASE_URL: z.string().url(),
    /**
     * Public IPv4 of the VPS. Custom-domain verify (issue #292) compares the
     * DNS A records observed for a hostname against this value before
     * flipping a Domain to ACTIVE — so a domain only goes live when DNS
     * actually points at us, which is also the precondition Let's Encrypt's
     * HTTP-01 challenge needs to succeed.
     */
    VPS_PUBLIC_IP: z.ipv4(),

    ANALYTICS_ENABLED: z
      .enum(["true", "false"])
      .default("true")
      .transform((v) => v === "true"),
    ANALYTICS_RETENTION_DAYS: z.coerce.number().int().positive().default(90),
    /**
     * Safety cap on the retention sweep — the job stops deleting once it has
     * removed this many rows in a single run, even if older rows still
     * remain. Prevents a runaway sweep from saturating the database when a
     * misconfiguration (e.g. retention dropped from 365 to 1) suddenly
     * makes hundreds of millions of rows eligible. Default 10M is large
     * enough that a healthy daily run never hits it; the next day's run
     * will pick up where this one stopped.
     */
    ANALYTICS_RETENTION_SAFETY_CAP: z.coerce.number().int().positive().default(10_000_000),
    /**
     * Stable salt mixed with the raw client IP before hashing into
     * `AnalyticsEvent.ipHash`. Per spec §14 Privacy ("avoid storing raw IP")
     * + issue #22 AC: required, never the empty string. Operators rotate
     * this salt periodically — see PR #22 doc-block: rotating the salt
     * deliberately invalidates the join key linking historical events to a
     * given visitor, which is acceptable because the same visitor's
     * `visitorId` cookie still ties their session events together.
     */
    ANALYTICS_IP_SALT: requiredString.min(16),

    GEOLOCATION_ENABLED: z
      .enum(["true", "false"])
      .default("false")
      .transform((v) => v === "true"),
    GEOIP_DB_PATH: trimmed.optional().default(""),
  })
  .superRefine((value, ctx) => {
    // Mode-conditional validation: openai requires AI_API_KEY. The mock
    // provider does not need a key — that's the whole point of the mock.
    if (value.AI_PROVIDER === "openai" && value.AI_API_KEY.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["AI_API_KEY"],
        message: "AI_API_KEY is required when AI_PROVIDER=openai",
      });
    }
  });

export type Env = z.infer<typeof envSchema>;
